import { EducatorStats } from "./EducatorStats";
import { DocumentorStats } from "./DocumentorStats";
import { InvestigatorStats } from "./InvestigatorStats";
import { AuditorStats } from "./AuditorStats";
import { RemediatorStats } from "./RemediatorStats";
import { ContinuerStats } from "./ContinuerStats";
import { MitigatorStats } from "./MitigatorStats";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getDocStatusList } from "../../store";
import {
  getSubscribedModules,
  getCurrentUser,
  getDepartments,
  getPriorities,
} from "@neumetric/blueprint-lib/store";
import ExportStatsDialog from "./ExportsFormDialog";
import {
  HeaderMenu,
  Col,
  getS3AssetUrl,
  Row,
} from "@neumetric/blueprint-lib/components";
import { useNavigate } from "react-router-dom";
import { DateTimeUtil } from "@neumetric/blueprint-lib/utility";
import {
  exportGroupToPDF,
  exportGroupToPNGInZip,
  exportGroupToCSV,
} from "../CentralStats/ExportsFormDialog/Helpers";
import NeumetricLogoImg from "../../assets/images/neumetric-logo.png";
import AuditorLogoImg from "../../assets/images/auditor-logo.png";
import DocumenterLogoImg from "../../assets/images/documenter-logo.png";
import EducatorLogoImg from "../../assets/images/educator-logo.png";
import InvestigatorLogoImg from "../../assets/images/investigator-logo.png";
import RemediatorLogoImg from "../../assets/images/remediator-logo.png";
import MitigatorLogoImg from "../../assets/images/mitigator-logo.png";
import ContinuerLogoImg from "../../assets/images/continuer-logo.png";
import FusionLogoImg from "../../assets/images/Fusion_Logo2.png";
import { OvalLoading } from "../../components";
import { centralActions } from "../../store/central";
import { ExportHeader } from "./ExportSection";
import "./style.scss";
import { ExportImageContext } from "./ExportImageContext";
import { getCurrentOrgLogo } from "../../store/central";

// ---- MAPPERS: dialog selections -> helper module keys ----
const ALL_EXPORT_KEYS = [
  "Documenter_Documentation_Status",
  "Remediator_Department_vs_Compliance_Status",
  "Remediator_Priority_vs_Compliance_Status",
  "Remediator_Department_vs_Priority_Status",
  "Auditor_Audit_Status",
  "Auditor_overview",
  "Educator_Assessment_vs_Status",
  "Educator_Department_vs_Status",
  "Educator_Overall_Coverage",
  "Investigator_Severity_vs_Incident_Status",
  "Investigator_Department_vs_Incident_Status",
  "Continuer_Continuity_Test_Status",
  "Mitigator_Decision_Status_vs_Risk_Status",
  "Mitigator_Department_vs_Severity_Status",
  "Mitigator_Status_vs_Severity_Status",
  "Mitigator_Department_vs_Status_Status",
];

const FUSION_EXPORT_KEYS = [
  "Documenter_Documentation_Status",
  "Remediator_Department_vs_Compliance_Status",
  "Remediator_Priority_vs_Compliance_Status",
  "Remediator_Department_vs_Priority_Status",
  "Auditor_Audit_Status",
  "Auditor_overview",
  "Educator_Assessment_vs_Status",
  "Educator_Department_vs_Status",
  "Educator_Overall_Coverage",
  "Investigator_Severity_vs_Incident_Status",
  "Investigator_Department_vs_Incident_Status",
  "Continuer_Continuity_Test_Status",
  "Mitigator_Decision_Status_vs_Risk_Status",
  "Mitigator_Department_vs_Severity_Status",
  "Mitigator_Status_vs_Severity_Status",
  "Mitigator_Department_vs_Status_Status",
];

const MODULE_GROUP_MAP = {
  fusion: FUSION_EXPORT_KEYS, // selecting Fusion  (not same as "Export All")
  remediator: [
    "Remediator_Department_vs_Compliance_Status",
    "Remediator_Priority_vs_Compliance_Status",
  ],
  auditor: ["Auditor_Audit_Status", "Auditor_overview"],
  documenter: ["Documenter_Documentation_Status"],
  continuer: ["Continuer_Continuity_Test_Status"],
  educator: ["Educator_Assessment_vs_Status", "Educator_Department_vs_Status"],
  investigator: [
    "Investigator_Severity_vs_Incident_Status",
    "Investigator_Department_vs_Incident_Status",
  ],
  mitigator: [
    "Mitigator_Decision_Status_vs_Risk_Status",
    "Mitigator_Department_vs_Severity_Status",
    "Mitigator_Status_vs_Severity_Status",
    "Mitigator_Department_vs_Status_Status",
  ],
};

function expandModuleSelections(selections) {
  if (!Array.isArray(selections) || selections.length === 0) return [];

  const out = new Set();

  if (selections.includes("export_all")) {
    // Add fusion (top overview) + all module pages
    out.add("fusion");
    ALL_EXPORT_KEYS.forEach((k) => out.add(k));
    return Array.from(out);
  }

  if (selections.includes("fusion")) {
    FUSION_EXPORT_KEYS.forEach((k) => out.add(k));
    return Array.from(out);
  }

  for (const sel of selections) {
    // Direct leaf match (chart key)
    if (ALL_EXPORT_KEYS.includes(sel)) {
      out.add(sel);
      continue;
    }

    // Parent module → expand using MODULE_GROUP_MAP
    const base = sel.replace(/_(overview|charts)$/i, "");
    const mapped = MODULE_GROUP_MAP[base];
    if (mapped) {
      mapped.forEach((k) => out.add(k));
      continue;
    }
  }

  return Array.from(out);
}

const dateRangeList = [
  { id: "last_7_days", label: "Last 7 Days" },
  { id: "last_week", label: "Last Week (Sun-Sat)" },
  { id: "last_30_days", label: "Last 30 Days" },
  { id: "last_month", label: "Previous Month" },
  { id: "last_90_days", label: "Last 90 Days" },
  { id: "last_year", label: "Previous Quarter" },
  { id: "last_365_days", label: "Last 365 Days" },
  { id: "previous_year", label: "Previous Year (Apr-Mar)" },
  { id: "over_all", label: "Entire History" },
];
// const priorities = ["Emergency", "Urgent", "Critical", "High", "Medium", "Low", "Minimal", "Least"];

// Timestamp like 2510091632  => YYMMDDHHMM
export const formatYYMMDDHHMM = (d = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const HH = pad(d.getHours());
  const MM = pad(d.getMinutes());
  return `${yy}${mm}${dd}${HH}${MM}`;
};

export function CentralStats() {
  const dispatch = useDispatch();
  const user = useSelector(getCurrentUser);
  const currentOrgLogo = useSelector(getCurrentOrgLogo);
  const orgLogoSrc = user?.org?.logo_url;
  const { downloadAndSaveImage } = useContext(ExportImageContext);

  const departments = useSelector(getDepartments);
  const statusList = useSelector(getDocStatusList);
  const priorities = useSelector(getPriorities);

  const navigate = useNavigate();
  const modules = useSelector(getSubscribedModules);
  const [selectedAudit, setSelectedAudit] = useState("");
  const [auditList, setAuditList] = useState([]);
  // NEW: carry auditor export selections to AuditorStats
  const [exportCtx, setExportCtx] = React.useState(null);

  const getTab = () => {
    const currentUrlParams = new URLSearchParams(window.location.search);
    return currentUrlParams.get("tab");
  };

  const getInitialModule = () => {
    const currentUrlParams = new URLSearchParams(window.location.search);
    return currentUrlParams.get("module") || "fusion";
  };

  const initialRange =
    dateRangeList.find((o) => o.id === "last_365_days") ?? dateRangeList[0];

  const [state, setState] = React.useState({
    tab: getTab(),
    menuTarget: null,
    selectedDialogId: null,
    selectedModule: getInitialModule(),
    showExport: false,
    dateRange: initialRange,
    exportEverything: false,
    exportFusion: false, // specifically if fusion_overview is selected
    exportModules: [],
    exportSingleModule: false,
  });

  const moduleProps = React.useMemo(
    () => ({
      user,
      departments,
      statusList,
      priorities,
      dateRange: state.dateRange,
      orgLogoSrc,
      dateRangeList,
    }),
    [user, departments, statusList, orgLogoSrc, state.dateRange],
  );

  const updateState = useCallback(
    (newState) => setState((_) => ({ ..._, ...newState })),
    [],
  );

  const getOverviewSubMenus = () => {
    const tabModuleIds = [
      "documenter",
      "auditor",
      "remediator",
      "educator",
      "continuer",
      "investigator",
      "mitigator",
    ];
    const fusionMenu = {
      label: "Fusion",
      id: "fusion",
      image: getS3AssetUrl("IconFusion.png"),
    };

    const menus = tabModuleIds.map((moduleId) => {
      const module = modules.find((m) => m.id === moduleId) || {};
      return {
        label: module.label || moduleId,
        id: moduleId,
        image: module.icon,
      };
    });
    return [fusionMenu, ...menus];
  };

  const getTabMenus = () => [
    { label: "Overview", id: "O", subMenu: getOverviewSubMenus() },
    { label: "Guide", id: "G" },
  ];

  const handleSubmenuClick = useCallback(
    (submenu) => {
      // HeaderMenu may pass a string id or an object; normalize to id
      const id = typeof submenu === "string" ? submenu : submenu?.id;
      if (id) updateState({ selectedModule: id });
    },
    [updateState],
  );

  const handleDateRangeChange = (_attr, value /* string id */) => {
    const selected =
      typeof value === "string"
        ? dateRangeList.find((o) => o.id === value)
        : value; // in case NMForm starts returning the object
    // if (selected) updateState({ dateRange: selected });
    if (selected) {
      setState((s) => ({ ...s, dateRange: selected }));
      dispatch(centralActions.setCurrentDateRange(selected));
    }
  };
  useEffect(() => {
    if (state.dateRange && state.dateRange.id) {
      dispatch(
        centralActions.fetchRemediatorStats({ dateRange: state.dateRange.id }),
      );
    }
  }, [state.dateRange]);

  const showDateRangeFilter =
    ["remediator", "continuer", "investigator"].indexOf(
      state.selectedModule,
    ) !== -1;

  useEffect(() => {
    if (currentOrgLogo) {
      downloadAndSaveImage(currentOrgLogo);
    }
  }, [currentOrgLogo]);

  const stripParentsIfChildrenPicked = (mods) => {
    // if a direct page leaf is selected, drop its parent module id
    const set = new Set(mods);
    const bases = new Set();

    // record bases of direct leaves
    mods.forEach((m) => {
      const leafBase = m.split("_")[0]; // e.g., "Remediator" from "Remediator_Department_vs_..."
      if (ALL_EXPORT_KEYS.includes(m)) bases.add(leafBase.toLowerCase());
    });

    // remove parent ids that match those bases
    mods.forEach((m) => {
      if (!/_/.test(m) && bases.has(m.toLowerCase())) set.delete(m);
    });

    return Array.from(set);
  };

  // --- helpers (near the top) ---
  const PARENTS = [
    "remediator",
    "auditor",
    "documenter",
    "continuer",
    "educator",
    "investigator",
    "mitigator",
  ];

  // Works for both leaf keys like "Remediator_Department_vs_..." and parent ids like "remediator"
  const parentModuleForKey = (k = "") => {
    const lk = String(k).toLowerCase();
    for (const p of PARENTS) {
      if (lk === p || lk.startsWith(`${p}_`)) return p;
    }
    return null;
  };

  const raf = () => new Promise((r) => requestAnimationFrame(r));
  const settleMount = async () => {
    // Give React a couple of frames, then nudge layout for charts
    await raf();
    await raf();
    window.dispatchEvent(new Event("resize"));
  };

  // --- handleExport ---
  const handleExport = async ({
    format,
    modules,
    idToLabelMap,
    parentLabelMap,
  }) => {
    const picked = Array.isArray(modules) ? modules : [];

    //  Collect selected leaf audits (like "SomeAudit_overview")
    const auditorLeafIds = picked.filter(
      (id) =>
        typeof id === "string" &&
        id.endsWith("_overview") &&
        id !== "Auditor_overview",
    );

    //  Always include parent overview when any audit leaves are picked
    const pickedWithOverview =
      auditorLeafIds.length > 0
        ? Array.from(new Set([...picked, "Auditor_overview"]))
        : picked;

    const parentAuditorSelected =
      picked.includes("auditor") || picked.includes("Auditor_overview");

    //  Build the export payload for the Auditor Overview
    const auditorExportPayload =
      auditorLeafIds.length > 0
        ? { mode: "selected", selectedLeafIds: auditorLeafIds } // export only these audits
        : parentAuditorSelected
          ? { mode: "first6" } // export first 6 audits
          : null;

    setExportCtx({ auditor: auditorExportPayload });

    // Keep expanded module list
    const sanitized = stripParentsIfChildrenPicked(pickedWithOverview);
    const expanded = expandModuleSelections(sanitized);

    if (!expanded.length) return;

    // Determine modules to render
    const neededModules = Array.from(
      new Set(expanded.map(parentModuleForKey).filter(Boolean)),
    );

    // Export mode flags
    const isExportEverything = sanitized.includes("export_all");
    const isExportFusionOnly =
      sanitized.length === 1 && sanitized.includes("fusion");
    const isExportSingleModule =
      sanitized.length === 1 &&
      !sanitized.includes("fusion") &&
      !sanitized.includes("export_all");

    const isMultiOrAll = sanitized.length > 1 || isExportEverything;

    const prev = state.selectedModule;
    // Decide target tab for safe export (avoid Fusion grid)
    // If multiple modules or export-all → switch to neutral tab
    const target =
      isMultiOrAll && prev === "fusion"
        ? "documenter"
        : prev || neededModules[0];

    const assets = {
      neumetricLogo: NeumetricLogoImg,
      orgLogoUrl: orgLogoSrc,
      moduleLogosByKey: {
        Remediator_Department_vs_Compliance_Status: isExportFusionOnly
          ? FusionLogoImg
          : RemediatorLogoImg,
        Remediator_Priority_vs_Compliance_Status: isExportFusionOnly
          ? FusionLogoImg
          : RemediatorLogoImg,
        Auditor_Audit_Status: isExportFusionOnly
          ? FusionLogoImg
          : AuditorLogoImg,
        Auditor_overview: isExportFusionOnly ? FusionLogoImg : AuditorLogoImg,
        Documenter_Documentation_Status: isExportFusionOnly
          ? FusionLogoImg
          : DocumenterLogoImg,
        Continuer_Continuity_Test_Status: isExportFusionOnly
          ? FusionLogoImg
          : ContinuerLogoImg,
        Educator_Assessment_vs_Status: isExportFusionOnly
          ? FusionLogoImg
          : EducatorLogoImg,
        Educator_Department_vs_Status: isExportFusionOnly
          ? FusionLogoImg
          : EducatorLogoImg,
        Educator_Overall_Coverage: isExportFusionOnly
          ? FusionLogoImg
          : EducatorLogoImg,
        Investigator_Severity_vs_Incident_Status: isExportFusionOnly
          ? FusionLogoImg
          : InvestigatorLogoImg,
        Investigator_Department_vs_Incident_Status: isExportFusionOnly
          ? FusionLogoImg
          : InvestigatorLogoImg,
        Mitigator_Decision_Status_vs_Risk_Status: isExportFusionOnly
          ? FusionLogoImg
          : MitigatorLogoImg,
        Mitigator_Department_vs_Severity_Status: isExportFusionOnly
          ? FusionLogoImg
          : MitigatorLogoImg,
        Mitigator_Status_vs_Severity_Status: isExportFusionOnly
          ? FusionLogoImg
          : MitigatorLogoImg,
        Mitigator_Department_vs_Status_Status: isExportFusionOnly
          ? FusionLogoImg
          : MitigatorLogoImg,
      },
    };

    const ts = formatYYMMDDHHMM();
    let pdfFileName = `Fusion_Central_${ts}`;

    const parentForNaming =
      !isExportEverything &&
        !isExportFusionOnly &&
        neededModules.length === 1 &&
        neededModules[0] !== "fusion"
        ? neededModules[0]
        : null;

    if (isExportEverything) {
      // "Fusion_Central_[[YYMMDDHHMM]]"
    } else if (isExportFusionOnly) {
      pdfFileName += `_${parentLabelMap.fusion}`;
    } else if (parentForNaming) {
      const label =
        parentLabelMap[parentForNaming] ||
        parentForNaming[0].toUpperCase() + parentForNaming.slice(1);
      pdfFileName += `_${label}`;
    } else {
      pdfFileName += `_Modules`;
    }

    let opts = {};
    if (format === "pdf") {
      opts = { pdfFileName, isExportFusionOnly };
    } else if (format === "png") {
      opts = { pdfFileName, isExportFusionOnly, idToLabelMap, parentLabelMap };
    } else if (format === "csv") {
      opts = {
        filterByType: "table",
        pdfFileName,
        idToLabelMap,
        parentLabelMap,
      };
    }

    updateState({
      exportModules:
        isExportEverything || isExportFusionOnly ? PARENTS : neededModules,
      exportEverything: isExportEverything,
      exportFusion: isExportFusionOnly,
      exportSingleModule: isExportSingleModule,
    });

    let switched = false;
    if (prev !== target) {
      updateState({ selectedModule: target });
      switched = true;
      await settleMount();
    }

    // Wait for layout + charts to render
    await new Promise((r) => setTimeout(r, 100));

    //  New FIX: wait until Auditor Overview export sections are rendered
    if (expanded.includes("Auditor_overview")) {
      let retries = 0;
      while (
        retries < 10 &&
        document.querySelectorAll("[id^='auditor_overview_export_']").length ===
        0
      ) {
        await new Promise((r) => setTimeout(r, 100));
        retries++;
      }
    }

    // Show loader
    const loaderEl = document.getElementById("loader-component");
    if (loaderEl) loaderEl.style.display = "block";

    try {
      if (format === "pdf") await exportGroupToPDF(expanded, opts, assets);
      else if (format === "png")
        await exportGroupToPNGInZip(expanded, opts, assets);
      else if (format === "csv") await exportGroupToCSV(expanded, opts);
    } finally {
      if (loaderEl) loaderEl.style.display = "none";

      // Restore previous tab
      updateState({
        exportModules: [],
        selectedModule: prev,
        exportEverything: false,
        exportFusion: false,
        exportSingleModule: false,
      });
      setExportCtx(null);

      if (switched) await settleMount();
    }
  };

  const showAll = state.selectedModule === "fusion";

  const getModeForModule = (mod) => {
    if (state.exportEverything) return "export_everything";
    else if (state.exportFusion) return "fusion";
    else if (state.exportSingleModule) return "full";
    else if (state.exportModules.includes(mod)) return "full";
    else if (showAll) return "fusion";
    else if (state.selectedModule === mod) return "full";
    else return "full";
  };

  const isModuleBeingExported = (moduleId) => {
    // If exportEverything → all modules render
    if (state.exportEverything) return true;

    // If Fusion Overview is selected → show all modules (fusion + export_everything)
    if (state.exportFusion) return true;

    // Otherwise, show only those explicitly chosen
    return state.exportModules?.includes(moduleId);
  };
  // Fusion grid layout only when user is in Fusion tab and NOT exporting
  const isFusionLayoutActive =
    showAll &&
    !state.exportEverything &&
    !state.exportFusion &&
    !state.exportSingleModule;

  // Export layout for everything EXCEPT pure Fusion overview
  const isExportLayoutActive =
    state.exportEverything ||
    state.exportFusion ||
    state.exportSingleModule ||
    state.exportModules.length > 1;

  return (
    <Col className="w-100 h-100 o-auto">
      <Col className="w-100 pt-48 standard-view">
        <Row className="w-100 pos-relative">
          <HeaderMenu
            tabItems={getTabMenus()}
            filters={[]}
            initialSubmenu={state.selectedModule}
            classes={{ root: "pr-20 pt-20 card-shadow" }}
            onTabChange={(tab) => {
              const key = (
                typeof tab === "string" ? tab : String(tab || "")
              ).toUpperCase();
              setState((_) => ({ ..._, tab: key }));
              if (key) navigate(`/C/${key}`);
            }}
            onSubmenuClick={handleSubmenuClick}
            newObject={{
              label: "Export",
              icon: "UPLOAD",
              onClick: () => updateState({ showExport: true }),
            }}
            filter={
              showDateRangeFilter
                ? {
                  form: {
                    className: "w-180-px",
                    formData: { dateRange: state.dateRange?.id },
                    fields: [
                      {
                        attribute: "dateRange",
                        typeRepresentation: "DROPDOWN",
                        options: dateRangeList,
                        classes: { root: "w-180-px mb-0" },
                      },
                    ],
                    onChange: handleDateRangeChange,
                  },
                }
                : undefined
            }
          />
          {showAll && (
            <div
              className="pos-absolute"
              style={{ bottom: "6px", right: "20px", zIndex: 1 }}
            >
              <span className="ff-exo2 fs-10 fw-400 text-filled line-22 ml-2">
                Status as on
                <span className="fw-700 ml-2">
                  {new DateTimeUtil().toDateFullYearString()}
                </span>
                .
              </span>
            </div>
          )}
        </Row>
      </Col>

      <Col
        className="w-100 o-auto flex-auto h-100"
        style={{ position: "relative" }}
      >
        <div
          style={{
            height: "90%",
            width: "100%",
            backgroundColor: "rgba(255, 255, 255, 1)",
            position: "fixed",
            display: "none",
            zIndex: "999",
          }}
          id="loader-component"
        >
          <OvalLoading />
        </div>

        <div
          className={
            isFusionLayoutActive
              ? "fusion-layout o-auto w-100 h-100"
              : isExportLayoutActive
                ? "export-layout o-auto w-100 h-100"
                : "o-auto w-100 h-100"
          }
        >
          {/* ---------- FUSION GRID LAYOUT ---------- */}
          {isFusionLayoutActive && (
            <>
              <div className="fusion-left">
                {(showAll || state.selectedModule === "auditor") && (
                  <AuditorStats
                    {...moduleProps}
                    selectedAudit={selectedAudit}
                    setSelectedAudit={setSelectedAudit}
                    mode={getModeForModule("auditor")}
                    setAuditList={setAuditList}
                    exportPayload={exportCtx?.auditor} // ← NEW
                  />
                )}
              </div>

              <div className="fusion-center">
                {(showAll || state.selectedModule === "documenter") && (
                  <DocumentorStats
                    {...moduleProps}
                    mode={getModeForModule("documenter")}
                  />
                )}
                {(showAll || state.selectedModule === "educator") && (
                  <EducatorStats
                    {...moduleProps}
                    mode={getModeForModule("educator")}
                  />
                )}
                {(showAll || state.selectedModule === "continuer") && (
                  <ContinuerStats
                    {...moduleProps}
                    mode={getModeForModule("continuer")}
                  />
                )}
                {(showAll || state.selectedModule === "investigator") && (
                  <InvestigatorStats
                    {...moduleProps}
                    mode={getModeForModule("investigator")}
                  />
                )}
                {(showAll || state.selectedModule === "mitigator") && (
                  <MitigatorStats
                    {...moduleProps}
                    mode={getModeForModule("mitigator")}
                  />
                )}
              </div>

              <div
                className="fusion-right h-100 o-auto"
                style={{ marginLeft: "-20px" }}
              >
                {(showAll || state.selectedModule === "remediator") && (
                  <RemediatorStats
                    {...moduleProps}
                    mode={getModeForModule("remediator")}
                  />
                )}
              </div>
            </>
          )}

          {/* ---------- EXPORT / SINGLE TAB LAYOUT ---------- */}
          {!isFusionLayoutActive && (
            <>
              {(isModuleBeingExported("auditor") ||
                state.selectedModule === "auditor" ||
                state.exportEverything) && (
                  <AuditorStats
                    {...moduleProps}
                    selectedAudit={selectedAudit}
                    setSelectedAudit={setSelectedAudit}
                    setAuditList={setAuditList}
                    mode={getModeForModule("auditor")}
                    exportPayload={exportCtx?.auditor} // ← NEW
                  />
                )}

              {(isModuleBeingExported("documenter") ||
                state.selectedModule === "documenter" ||
                state.exportEverything) && (
                  <DocumentorStats
                    {...moduleProps}
                    mode={getModeForModule("documenter")}
                  />
                )}

              {(isModuleBeingExported("educator") ||
                state.selectedModule === "educator" ||
                state.exportEverything) && (
                  <EducatorStats
                    {...moduleProps}
                    mode={getModeForModule("educator")}
                  />
                )}

              {(isModuleBeingExported("continuer") ||
                state.selectedModule === "continuer" ||
                state.exportEverything) && (
                  <ContinuerStats
                    {...moduleProps}
                    mode={getModeForModule("continuer")}
                  />
                )}

              {(isModuleBeingExported("investigator") ||
                state.selectedModule === "investigator" ||
                state.exportEverything) && (
                  <InvestigatorStats
                    {...moduleProps}
                    mode={getModeForModule("investigator")}
                  />
                )}

              {(isModuleBeingExported("remediator") ||
                state.selectedModule === "remediator" ||
                state.exportEverything) && (
                  <RemediatorStats
                    {...moduleProps}
                    mode={getModeForModule("remediator")}
                  />
                )}
              {(isModuleBeingExported("mitigator") ||
                state.selectedModule === "mitigator" ||
                state.exportEverything) && (
                  <MitigatorStats mode={getModeForModule("mitigator")} />
                )}
            </>
          )}
        </div>
      </Col>

      {state.showExport && (
        <ExportStatsDialog
          open={state.showExport}
          onClose={() => updateState({ showExport: false })}
          onExport={handleExport}
          defaultFormat="pdf"
          defaultModules={["fusion"]}
          selectedAudit={selectedAudit}
          auditList={auditList}
        />
      )}
      {/* --- Hidden headers for export --- */}
      {/* <ExportHeader moduleKey="remediator" isFusionOnly={state.exportFusion} />
      <ExportHeader moduleKey="auditor" isFusionOnly={state.exportFusion} />
      <ExportHeader moduleKey="documenter" isFusionOnly={state.exportFusion} />
      <ExportHeader moduleKey="continuer" isFusionOnly={state.exportFusion} />
      <ExportHeader moduleKey="educator" isFusionOnly={state.exportFusion} />
      <ExportHeader
        moduleKey="investigator"
        isFusionOnly={state.exportFusion}
      />
      <ExportHeader moduleKey="fusion" isFusionOnly={state.exportFusion} /> */}
    </Col>
  );
}
